import { FC, PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from "react";
import { UUID } from "crypto";

import { cleanupOrphanedModalArtifacts } from "./cleanup-orphaned-modal-artifacts";

// react-bootstrap's dialog transition is 300ms; wait past it before sweeping anything up.
const MODAL_EXIT_GRACE_MS = 400;

interface ModalContextProps {
  recordModalOpen: (modal: UUID, isOpen: boolean) => void;
  /** Adds or removes a modal from the app-wide stack, which drives modal and backdrop z-index. */
  setModalStacked: (modal: string, isOpen: boolean) => void;
  /** Ids of the modals that are currently open, in the order they were opened. */
  openModals: readonly string[];
}

export const ModalContext = createContext<ModalContextProps>({
  /* eslint-disable @typescript-eslint/no-empty-function */
  recordModalOpen: () => {},
  setModalStacked: () => {},
  /* eslint-enable @typescript-eslint/no-empty-function */
  openModals: []
});

export const ModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [modalOpenRecord, setModalOpenRecord] = useState<Record<string, boolean>>({});
  const [openModals, setOpenModals] = useState<readonly string[]>([]);

  const isModalOpen = useMemo(
    () => Object.entries(modalOpenRecord).some(([, value]) => value === true),
    [modalOpenRecord]
  );

  useEffect(() => {
    window.toasts.isModalOpen = isModalOpen;
  }, [isModalOpen]);

  const recordModalOpen = useCallback((modal: UUID, isOpen: boolean) => {
    setModalOpenRecord(oldState => ({ ...oldState, [modal]: isOpen }));
  }, []);

  const setModalStacked = useCallback((modal: string, isOpen: boolean) => {
    setOpenModals(oldStack => {
      if (isOpen) {
        return oldStack.includes(modal) ? oldStack : [...oldStack, modal];
      }
      return oldStack.includes(modal) ? oldStack.filter(id => id !== modal) : oldStack;
    });
  }, []);

  // Backdrops left behind by a modal that unmounted while open would otherwise pile up for
  // the rest of the session, blocking clicks across the whole app.
  useEffect(() => {
    if (openModals.length > 0) {
      return;
    }

    const timer = window.setTimeout(cleanupOrphanedModalArtifacts, MODAL_EXIT_GRACE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [openModals]);

  const context = useMemo(
    () => ({ recordModalOpen, setModalStacked, openModals }),
    [recordModalOpen, setModalStacked, openModals]
  );

  return <ModalContext.Provider value={context}>{children}</ModalContext.Provider>;
};
